import { Ref, forwardRef } from "react"
import { FormikHelpers, FormikProps } from "formik"
import { Formik } from "../../../../../shared/components/forms/SchemaFormik"
import * as yup from "yup"
import { StandardFormikErrorScrollWrapper } from "../../../../../shared/components/forms/StandardFormikErrorScrollWrapper"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { toCurrency, toDineroLossy, toMonetaryAmount } from "../../../../currency/currencyLib"
import { CreateUpdateExtraServiceFormContent } from "./CreateUpdateExtraServiceFormContent"
import {
    CreateUpdateExtraServiceFormValues,
    ExternalServiceEntry,
    ExtraServiceType,
    ExtraServicePricingType,
    ExtraServiceActionType,
} from "../extraServicesTypes"
import { useDateTime } from "../../../../localization/useDateTime"
import { DaysOfWeek } from "../../../merchant/menuPlanning/menuPlanningTypes"
import { useMerchant } from "../../../../merchants/merchantHooks"

type CreateUpdateProductFormProps = Readonly<{
    initialValues?: CreateUpdateExtraServiceFormValues | null
    onSubmit: (formData: ExternalServiceEntry) => void
}>
type FormikRef = Ref<FormikProps<CreateUpdateExtraServiceFormValues>>

const defaultInitialValues: CreateUpdateExtraServiceFormValues = {
    name: "",
    action: "",
    productGroupCode: undefined,
    description: "",
    isActive: true,
    isMeetingTimeRequired: false,
    isMeetingLocationRequired: false,
    pricingType: "",
    serviceType: "",
    priceList: null,
    serviceTime: {
        weekDays: [],
        timeInterval: undefined,
    },
    minimumPrice: undefined,
    maximumPrice: undefined,
    pricePerSeat: undefined,
    minimumPriceCurrency: "DKK",
    maximumPriceCurrency: "DKK",
    pricePerSeatCurrency: "DKK",
    defaultPrice: undefined,
    defaultCurrency: "DKK",
}

export const CreateUpdateExtraServiceForm = forwardRef(
    ({ initialValues, onSubmit }: CreateUpdateProductFormProps, ref: FormikRef) => {
        const { l10n } = useAppLocalization()
        const dateTimeFactory = useDateTime()
        const { merchantId } = useMerchant()

        const requiredFieldMessage = l10n.getString("required-field")

        const validationSchema = yup.object({
            name: yup.string().required(requiredFieldMessage),
            description: yup
                .string()
                .max(
                    500,
                    l10n.getString("extra-services-create-service-description-field-max-length", { maxLength: 500 }),
                ),
            isActive: yup.boolean(),
            serviceType: yup.string().oneOf(Object.values(ExtraServiceType)).required(requiredFieldMessage),
            pricingType: yup.string().oneOf(Object.values(ExtraServicePricingType)).required(requiredFieldMessage),
            priceList: yup
                .object()
                .shape({
                    id: yup.number(),
                })
                .nullable()
                .required(requiredFieldMessage),
            defaultPrice: yup.number().when("pricingType", {
                is: ExtraServicePricingType.FLAT_FEE,
                then: (schema) => schema.required(requiredFieldMessage),
            }),
            defaultCurrency: yup.string().when("pricingType", {
                is: ExtraServicePricingType.FLAT_FEE,
                then: (schema) => schema.required(requiredFieldMessage),
            }),
            pricePerSeat: yup.number().when("pricingType", {
                is: ExtraServicePricingType.PER_SEAT,
                then: (schema) => schema.required(requiredFieldMessage),
            }),
            pricePerSeatCurrency: yup.string().when("pricingType", {
                is: ExtraServicePricingType.PER_SEAT,
                then: (schema) => schema.required(requiredFieldMessage),
            }),
            minimumPrice: yup.number().when("pricingType", {
                is: ExtraServicePricingType.PER_SEAT,
                then: (schema) => schema.required(requiredFieldMessage),
            }),
            minimumPriceCurrency: yup.string().when("pricingType", {
                is: ExtraServicePricingType.PER_SEAT,
                then: (schema) => schema.required(requiredFieldMessage),
            }),
            maximumPrice: yup.number().when("pricingType", {
                is: ExtraServicePricingType.PER_SEAT,
                then: (schema) => schema.required(requiredFieldMessage),
            }),
            maximumPriceCurrency: yup.string().when("pricingType", {
                is: ExtraServicePricingType.PER_SEAT,
                then: (schema) => schema.required(requiredFieldMessage),
            }),
            serviceTime: yup
                .object({
                    weekDays: yup.array().of(yup.string().oneOf(Object.values(DaysOfWeek))),
                    timeInterval: yup.object({
                        start: yup.date(),
                        end: yup.date().when("start", {
                            is: (start: Date | undefined) => !!start,
                            then: (schema) => schema.required(requiredFieldMessage),
                        }),
                    }),
                })
                .test(
                    "weekdays-required-when-time",
                    l10n.getString("extra-services-create-service-time-weekdays-min-error"),
                    (value) => {
                        const hasTime = value?.timeInterval?.start || value?.timeInterval?.end
                        const hasWeekDays = value?.weekDays && value.weekDays.length > 0
                        if (hasTime && !hasWeekDays) return false
                        return true
                    },
                )
                .test("time-required-when-weekdays", "", function (value) {
                    const hasWeekDays = value?.weekDays && value.weekDays.length > 0
                    if (!hasWeekDays) return true

                    const errors: yup.ValidationError[] = []
                    if (!value?.timeInterval?.start) {
                        errors.push(
                            this.createError({ path: "serviceTime.timeInterval.start", message: requiredFieldMessage }),
                        )
                    }
                    if (!value?.timeInterval?.end) {
                        errors.push(
                            this.createError({ path: "serviceTime.timeInterval.end", message: requiredFieldMessage }),
                        )
                    }
                    if (errors.length > 0) {
                        throw new yup.ValidationError(errors)
                    }
                    return true
                }),
            isMeetingTimeRequired: yup.boolean(),
            isMeetingLocationRequired: yup.boolean(),
            action: yup.mixed<ExtraServiceActionType>().oneOf(Object.values(ExtraServiceActionType)).notRequired(),
        })

        function handleSubmit(
            values: CreateUpdateExtraServiceFormValues,
            _helpers: FormikHelpers<CreateUpdateExtraServiceFormValues>,
        ) {
            const {
                isActive,
                action,
                isMeetingTimeRequired,
                isMeetingLocationRequired,
                serviceTime,
                productGroupCode,
                priceList,
                name,
                description,
                serviceType,
                pricingType,
            } = values

            const productGroupCodeBody = productGroupCode
                ? {
                      productGroupCode: {
                          id: productGroupCode.id,
                      },
                  }
                : {}

            const availabilityDetails =
                serviceTime.timeInterval && serviceTime.timeInterval.end
                    ? {
                          weekDays: serviceTime.weekDays,
                          timeInterval: {
                              start: dateTimeFactory.toHourMinute(serviceTime.timeInterval.start),
                              end: dateTimeFactory.toHourMinute(serviceTime.timeInterval.end),
                          },
                      }
                    : undefined

            const commonFields = {
                serviceType: serviceType as ExtraServiceType,
                name,
                description,
                isActive,
                action: action || undefined,
                isMeetingTimeRequired,
                isMeetingLocationRequired,
                ...productGroupCodeBody,
                priceLists: priceList ? [{ id: priceList.id }] : [],
                merchant: { id: merchantId },
                ...(availabilityDetails ? { availabilityDetails } : {}),
            }

            const modifyData: ExternalServiceEntry =
                pricingType === ExtraServicePricingType.PER_SEAT
                    ? {
                          ...commonFields,
                          pricingType: ExtraServicePricingType.PER_SEAT,
                          minimumPrice: toMonetaryAmount(
                              toDineroLossy(values.minimumPrice!, toCurrency(values.minimumPriceCurrency)),
                          ),
                          maximumPrice: toMonetaryAmount(
                              toDineroLossy(values.maximumPrice!, toCurrency(values.maximumPriceCurrency)),
                          ),
                          pricePerSeat: toMonetaryAmount(
                              toDineroLossy(values.pricePerSeat!, toCurrency(values.pricePerSeatCurrency)),
                          ),
                      }
                    : {
                          ...commonFields,
                          pricingType: ExtraServicePricingType.FLAT_FEE,
                          price: toMonetaryAmount(
                              toDineroLossy(values.defaultPrice!, toCurrency(values.defaultCurrency)),
                          ),
                      }

            onSubmit({
                ...modifyData,
            })
        }

        return (
            <>
                <Formik<CreateUpdateExtraServiceFormValues>
                    enableReinitialize
                    initialValues={initialValues ?? defaultInitialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    innerRef={ref}
                    validateOnBlur={true}
                    validateOnChange={false}
                >
                    <StandardFormikErrorScrollWrapper<CreateUpdateExtraServiceFormValues>
                        children={<CreateUpdateExtraServiceFormContent />}
                    />
                </Formik>
            </>
        )
    },
)
